import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import PostComments from './PostComments';
import FBPostComments from './FBPostComments';
import { notify } from '../utils/notify';
import './AnalyticsDashboard.css';

const COLORS = {
  positive: '#4CAF50',
  neutral: '#FFC107',
  negative: '#F44336',
  mixed: '#9C27B0'
};

function AnalyticsDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sentimentTrends, setSentimentTrends] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7); // days
  const [selectedAgent, setSelectedAgent] = useState('customer_care');
  const [agentLogs, setAgentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'last_message_at', direction: 'desc' });
  const [commentLogs, setCommentLogs] = useState([]);
  const [commentLogsLoading, setCommentLogsLoading] = useState(false);
  const [hotLeadsOnly, setHotLeadsOnly] = useState(false);
  const [socialCounters, setSocialCounters] = useState(null);
  const [commentModal, setCommentModal] = useState(null);
  const [commentModalLoading, setCommentModalLoading] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, trendsRes, agentsRes, socialCountersRes] = await Promise.all([
        apiClient.get(`/api/v1/analytics/dashboard?days=${timeRange}`),
        apiClient.get(`/api/v1/analytics/sentiment-trends?days=${timeRange}`),
        apiClient.get(`/api/v1/analytics/agents/performance?days=${timeRange}`),
        apiClient.get(`/api/v1/analytics/social-counters?days=${timeRange}`)
      ]);

      setStats(dashboardRes.data);
      setSentimentTrends(trendsRes.data.trends);
      setAgentPerformance(agentsRes.data.agents);
      setSocialCounters(socialCountersRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Fetch logs for selected agent
  const fetchAgentLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await apiClient.get(`/api/v1/analytics/agents/logs?agent_type=${selectedAgent}&days=${timeRange}`);
      setAgentLogs(res.data.logs);
    } catch (error) {
      console.error('Failed to fetch agent logs:', error);
      setAgentLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [selectedAgent, timeRange]);

  // Fetch comment logs for social engagement section
  const fetchCommentLogs = useCallback(async () => {
    setCommentLogsLoading(true);
    try {
      const res = await apiClient.get(
        `/api/v1/analytics/comment-logs?days=${timeRange}&limit=50&hot_leads_only=${hotLeadsOnly}`
      );
      setCommentLogs(res.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comment logs:', error);
      setCommentLogs([]);
    } finally {
      setCommentLogsLoading(false);
    }
  }, [timeRange, hotLeadsOnly]);

  const openCommentThread = useCallback(async (log) => {
    if (commentModalLoading) return;
    if (log.platform !== 'instagram' && log.platform !== 'facebook') return;
    if (!log.parent_post_db_id) return;

    setCommentModalLoading(true);
    try {
      const res = await apiClient.get(`/api/v1/posts/posts/${log.parent_post_db_id}/meta`);
      setCommentModal({
        platform: log.platform,
        postId: log.parent_post_db_id,
        postCaption: res.data.caption || '',
        initialAiPaused: !!res.data.ai_replies_paused,
      });
    } catch (err) {
      console.error('Failed to load post meta:', err);
      notify.error(err.response?.data?.detail || t('comments.loadError', 'Failed to load post'));
    } finally {
      setCommentModalLoading(false);
    }
  }, [commentModalLoading, t]);

  const closeCommentThread = useCallback(() => {
    setCommentModal(null);
    fetchCommentLogs();
  }, [fetchCommentLogs]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchAgentLogs();
  }, [fetchAgentLogs]);

  useEffect(() => {
    fetchCommentLogs();
  }, [fetchCommentLogs]);

  // Sort handler for DataTable
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sorted logs based on sortConfig
  const sortedLogs = [...agentLogs].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aVal = a[key];
    let bVal = b[key];

    // Handle null values
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    // Handle dates
    if (key === 'last_message_at' || key === 'created_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }

    // Handle numbers
    if (key === 'message_count') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }

    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }

    // Number comparison
    return direction === 'asc' ? aVal - bVal : bVal - aVal;
  });

  if (loading) {
    return <div className="container"><div className="loading">{t('analytics.loading')}</div></div>;
  }

  if (!stats) {
    return <div className="container"><div className="error">{t('analytics.no_data')}</div></div>;
  }

  // Prepare data for charts - filter out zero values to prevent label overlap
  const sentimentPieData = [
    { name: t('analytics.sentiment.positive'), value: stats.conversations.sentiment_breakdown.positive, color: COLORS.positive },
    { name: t('analytics.sentiment.neutral'), value: stats.conversations.sentiment_breakdown.neutral, color: COLORS.neutral },
    { name: t('analytics.sentiment.negative'), value: stats.conversations.sentiment_breakdown.negative, color: COLORS.negative }
  ].filter(item => item.value > 0);

  const trendChartData = sentimentTrends.map(trend => ({
    date: new Date(trend.date).toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-US', { month: 'short', day: 'numeric' }),
    positive: trend.sentiment_distribution.positive,
    neutral: trend.sentiment_distribution.neutral,
    negative: trend.sentiment_distribution.negative,
    avgScore: trend.average_score
  }));

  return (
    <div className="container analytics-dashboard">
      <div className="dashboard-header">
        <div className="title-with-back">
          <button className="back-button" onClick={() => navigate('/dashboard')} title={t('common.back_to_dashboard')}>
            <i className="bi bi-arrow-left"></i>
          </button>
          <h1>{t('analytics.title')}</h1>
        </div>
        <div className="time-range-selector">
          <button 
            className={timeRange === 7 ? 'active' : ''}
            onClick={() => setTimeRange(7)}
          >
            {t('analytics.time_range.days', { count: 7 })}
          </button>
          <button 
            className={timeRange === 30 ? 'active' : ''}
            onClick={() => setTimeRange(30)}
          >
            {t('analytics.time_range.days', { count: 30 })}
          </button>
          <button 
            className={timeRange === 90 ? 'active' : ''}
            onClick={() => setTimeRange(90)}
          >
            {t('analytics.time_range.days', { count: 90 })}
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card">
          <h3>{t('analytics.metrics.total_conversations')}</h3>
          <div className="metric-value">{stats.conversations.total}</div>
          <div className="metric-label">{t('analytics.metrics.last_days', { days: timeRange })}</div>
        </div>
        
        <div className="metric-card">
          <h3>{t('analytics.metrics.avg_sentiment')}</h3>
          <div className={`metric-value ${stats.conversations.average_sentiment_score > 0 ? 'positive' : 'negative'}`}>
            {stats.conversations.average_sentiment_score.toFixed(2)}
          </div>
          <div className="metric-label">{t('analytics.metrics.sentiment_scale')}</div>
        </div>

        <div className="metric-card">
          <h3>{t('analytics.metrics.escalations')}</h3>
          <div className="metric-value warning">{stats.conversations.escalations}</div>
          <div className="metric-label">
            {((stats.conversations.escalations / stats.conversations.total) * 100).toFixed(1)}% {t('analytics.metrics.rate')}
          </div>
        </div>

        <div className="metric-card">
          <h3>{t('analytics.metrics.avg_response_time')}</h3>
          <div className="metric-value">
            {Math.floor(stats.conversations.average_response_time_seconds / 60)}m
          </div>
          <div className="metric-label">{t('analytics.metrics.minutes_per_response')}</div>
        </div>
      </div>

      {/* Sentiment Distribution */}
      <div className="chart-row">
        <div className="card chart-card">
          <h3>{t('analytics.charts.sentiment_distribution')}</h3>
          {sentimentPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sentimentPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sentimentPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-chart">
              <span>📊</span>
              <p>{t('analytics.charts.no_sentiment_data')}</p>
            </div>
          )}
        </div>

        <div className="card chart-card">
          <h3>{t('analytics.charts.sentiment_trends')}</h3>
          {trendChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="positive" name={t('analytics.sentiment.positive')} stroke={COLORS.positive} strokeWidth={2} />
                <Line type="monotone" dataKey="neutral" name={t('analytics.sentiment.neutral')} stroke={COLORS.neutral} strokeWidth={2} />
                <Line type="monotone" dataKey="negative" name={t('analytics.sentiment.negative')} stroke={COLORS.negative} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-chart">
              <span>📈</span>
              <p>{t('analytics.charts.no_trends_data')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Agent Performance & Logs */}
      <div className="card">
        <h3>{t('analytics.agent_performance.title')}</h3>
        
        {/* Agent Type Selector - Show all agents including content (Instagram DM) */}
        <div className="agent-selector">
          <button 
            className={`agent-selector-btn ${selectedAgent === 'customer_care' ? 'active' : ''}`}
            onClick={() => setSelectedAgent('customer_care')}
          >
            {t('analytics.agent_performance.customer_care')}
          </button>
          <button 
            className={`agent-selector-btn ${selectedAgent === 'tutor' ? 'active' : ''}`}
            onClick={() => setSelectedAgent('tutor')}
          >
            {t('analytics.agent_performance.tutor')}
          </button>
          <button 
            className={`agent-selector-btn ${selectedAgent === 'content' ? 'active' : ''}`}
            onClick={() => setSelectedAgent('content')}
          >
            📸 {t('analytics.agent_performance.content', 'Instagram DM')}
          </button>
        </div>

        <div className="agent-performance-grid">
          {agentPerformance.filter(agent => agent.agent_type === selectedAgent).map((agent, index) => (
            <div key={index} className="agent-card selected">
              <h4>{agent.agent_type === 'customer_care' ? t('analytics.agent_performance.customer_care') : agent.agent_type === 'tutor' ? t('analytics.agent_performance.tutor') : agent.agent_type === 'content' ? `📸 ${t('analytics.agent_performance.content', 'Instagram DM')}` : agent.agent_type || t('analytics.agent_performance.unknown')}</h4>
              <div className="agent-stats">
                <div className="stat">
                  <span className="stat-label">{t('analytics.agent_performance.conversations')}:</span>
                  <span className="stat-value">{agent.total_conversations}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">{t('analytics.agent_performance.avg_sentiment')}:</span>
                  <span className={`stat-value ${agent.average_sentiment > 0 ? 'positive' : 'negative'}`}>
                    {agent.average_sentiment.toFixed(2)}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-label">{t('analytics.agent_performance.escalation_rate')}:</span>
                  <span className="stat-value warning">{agent.escalation_rate.toFixed(1)}%</span>
                </div>
                <div className="stat">
                  <span className="stat-label">{t('analytics.agent_performance.avg_response')}:</span>
                  <span className="stat-value">{Math.floor(agent.average_response_time / 60)}m</span>
                </div>
              </div>
            </div>
          ))}
          {/* Show placeholder when no data for selected agent */}
          {agentPerformance.filter(agent => agent.agent_type === selectedAgent).length === 0 && (
            <div className="agent-card selected no-data">
              <h4>{selectedAgent === 'customer_care' ? t('analytics.agent_performance.customer_care') : selectedAgent === 'content' ? `📸 ${t('analytics.agent_performance.content', 'Instagram DM')}` : t('analytics.agent_performance.tutor')}</h4>
              <div className="agent-stats">
                <p className="no-data-message">{t('analytics.agent_logs.no_conversations')}</p>
              </div>
            </div>
          )}
        </div>
        <div className="agent-logs-section">
          <h4>{t('analytics.agent_logs.conversations_for')}: {selectedAgent === 'customer_care' ? t('analytics.agent_performance.customer_care') : selectedAgent === 'content' ? `📸 ${t('analytics.agent_performance.content', 'Instagram DM')}` : t('analytics.agent_performance.tutor')}</h4>
          {logsLoading ? (
            <div>{t('analytics.agent_logs.loading')}</div>
          ) : (
            <div className="conversations-table-wrapper">
              {agentLogs.length === 0 ? (
                <div className="no-logs">{t('analytics.agent_logs.no_conversations')}</div>
              ) : (
                <table className="conversations-datatable">
                  <thead>
                    <tr>
                      <th 
                        className={`sortable ${sortConfig.key === 'contact_name' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('contact_name')}
                      >
                        {t('analytics.table.contact')} {sortConfig.key === 'contact_name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th 
                        className={`sortable ${sortConfig.key === 'platform' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('platform')}
                      >
                        {t('analytics.table.platform')} {sortConfig.key === 'platform' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th 
                        className={`sortable ${sortConfig.key === 'overall_sentiment' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('overall_sentiment')}
                      >
                        {t('analytics.table.sentiment')} {sortConfig.key === 'overall_sentiment' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th 
                        className={`sortable ${sortConfig.key === 'message_count' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('message_count')}
                      >
                        {t('analytics.table.messages')} {sortConfig.key === 'message_count' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th 
                        className={`sortable ${sortConfig.key === 'last_message_at' ? sortConfig.direction : ''}`}
                        onClick={() => handleSort('last_message_at')}
                      >
                        {t('analytics.table.last_message')} {sortConfig.key === 'last_message_at' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th>{t('analytics.table.status')}</th>
                      <th>{t('analytics.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLogs.map(log => (
                      <tr key={log.id}>
                        <td className="contact-cell">
                          <span className="contact-name">{log.contact_name || log.contact_id}</span>
                        </td>
                        <td>
                          <span className={`platform-badge ${log.platform?.toLowerCase()}`}>
                            {log.platform}
                          </span>
                        </td>
                        <td>
                          <span className={`sentiment-badge ${log.overall_sentiment}`}>
                            {log.overall_sentiment || 'N/A'}
                          </span>
                        </td>
                        <td className="message-count-cell">{log.message_count}</td>
                        <td className="date-cell">
                          {log.last_message_at 
                            ? new Date(log.last_message_at).toLocaleString() 
                            : new Date(log.created_at).toLocaleString()}
                        </td>
                        <td>
                          {log.was_escalated && <span className="escalated-badge">⚠ {t('analytics.table.escalated')}</span>}
                        </td>
                        <td>
                          <button 
                            className="btn-view-detail"
                            onClick={() => navigate(`/analytics/conversation/${log.id}`)}
                          >
                            📋 {t('analytics.table.details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Social Media Engagement */}
      <div className="card">
        <h3>📊 {t('analytics.social.title')}</h3>
        
        {/* Counters Grid */}
        <div className="social-engagement-grid">
          <div className="social-metric-card">
            <div className="social-metric-icon comments-icon"><i className="bi bi-chat-square-text"></i></div>
            <div className="social-metric-value">{socialCounters?.total_comments ?? stats.social_interactions.total}</div>
            <div className="social-metric-label">{t('analytics.social.total_comments', 'Total Comments')}</div>
            {socialCounters?.platform_breakdown && (
              <div className="platform-breakdown">
                {socialCounters.platform_breakdown.instagram > 0 && <span className="platform-badge instagram">📸 {socialCounters.platform_breakdown.instagram}</span>}
                {socialCounters.platform_breakdown.facebook > 0 && <span className="platform-badge facebook">📘 {socialCounters.platform_breakdown.facebook}</span>}
                {socialCounters.platform_breakdown.tiktok > 0 && <span className="platform-badge tiktok">🎵 {socialCounters.platform_breakdown.tiktok}</span>}
              </div>
            )}
          </div>
          <div className="social-metric-card">
            <div className="social-metric-icon ai-icon"><i className="bi bi-robot"></i></div>
            <div className="social-metric-value">{socialCounters?.auto_replied ?? 0}</div>
            <div className="social-metric-label">{t('analytics.social.auto_replied', 'AI Auto-Replies')}</div>
          </div>
          <div className="social-metric-card">
            <div className="social-metric-icon unreplied-icon"><i className="bi bi-chat-left-dots"></i></div>
            <div className="social-metric-value">{socialCounters?.unreplied ?? 0}</div>
            <div className="social-metric-label">{t('analytics.social.unreplied', 'Unreplied')}</div>
          </div>
          <div className="social-metric-card">
            <div className="social-metric-icon positive-icon"><i className="bi bi-emoji-smile"></i></div>
            <div className="social-metric-value">{socialCounters?.sentiment?.positive ?? stats.social_interactions.positive_interactions}</div>
            <div className="social-metric-label">{t('analytics.sentiment.positive')}</div>
          </div>
          <div className="social-metric-card">
            <div className="social-metric-icon negative-icon"><i className="bi bi-emoji-frown"></i></div>
            <div className="social-metric-value">{socialCounters?.sentiment?.negative ?? stats.social_interactions.negative_interactions}</div>
            <div className="social-metric-label">{t('analytics.sentiment.negative')}</div>
          </div>
          <div className="social-metric-card">
            <div className="social-metric-icon engagement-icon"><i className="bi bi-bar-chart"></i></div>
            <div className="social-metric-value">{stats.social_interactions.average_engagement.toFixed(2)}</div>
            <div className="social-metric-label">{t('analytics.social.avg_engagement')}</div>
          </div>
        </div>

        {/* Comment Logs Table */}
        <div className="comment-logs-section">
          <h4><i className="bi bi-journal-text"></i> {t('analytics.social.comment_logs', 'Comment Logs')}</h4>
          <label className="hot-leads-toggle">
            <input
              type="checkbox"
              checked={hotLeadsOnly}
              onChange={(e) => setHotLeadsOnly(e.target.checked)}
            />
            <span>{t('hotLeads.filter_only', 'Only hot leads')}</span>
          </label>
          {commentLogsLoading ? (
            <div className="loading-small">{t('common.loading')}</div>
          ) : commentLogs.length === 0 ? (
            <div className="no-logs">{t('analytics.social.no_comments', 'No comments found for this period')}</div>
          ) : (
            <div className="conversations-table-wrapper">
              <table className="conversations-datatable comment-logs-table">
                <thead>
                  <tr>
                    <th>{t('analytics.social.col_platform', 'Platform')}</th>
                    <th>{t('analytics.social.col_user', 'User')}</th>
                    <th>{t('analytics.social.col_comment', 'Comment')}</th>
                    <th>{t('analytics.social.col_ai_response', 'AI Response')}</th>
                    <th>{t('analytics.table.sentiment')}</th>
                    <th>{t('analytics.social.col_status', 'Status')}</th>
                    <th>{t('analytics.social.col_date', 'Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {commentLogs.map(log => {
                    const canOpen = (log.platform === 'instagram' || log.platform === 'facebook') && !!log.parent_post_db_id;
                    const rowClasses = [
                      log.lead_temperature === 'hot' ? 'hot-lead-row' : '',
                      canOpen ? 'clickable-row' : 'not-clickable-row',
                    ].filter(Boolean).join(' ');
                    const rowTitle = canOpen
                      ? t('comments.openThread', 'Open comment thread')
                      : log.platform === 'tiktok'
                        ? t('comments.tiktokReadOnly', 'TikTok threads are read-only')
                        : t('comments.postNotTracked', 'Post not tracked');
                    return (
                    <tr
                      key={log.id}
                      className={rowClasses}
                      title={rowTitle}
                      onClick={canOpen ? () => openCommentThread(log) : undefined}
                    >
                      <td>
                        <span className={`platform-badge ${log.platform || 'instagram'}`}>
                          {log.platform === 'facebook' ? '📘 Facebook' : log.platform === 'tiktok' ? '🎵 TikTok' : '📸 Instagram'}
                        </span>
                      </td>
                      <td className="contact-cell">
                        <span className="contact-name">@{log.username || '—'}</span>
                        {log.lead_temperature === 'hot' && (
                          <span
                            className="hot-lead-badge"
                            title={t('hotLeads.badge_tooltip', 'Hot lead — keyword match')}
                          >
                            🔥 {t('hotLeads.badge', 'HOT')}
                          </span>
                        )}
                        {log.lead_temperature === 'hot' && log.extracted_contacts && (
                          <div className="hot-lead-contacts">
                            {Array.isArray(log.extracted_contacts.email) &&
                              log.extracted_contacts.email.map((email) => (
                                <a
                                  key={`email-${email}`}
                                  href={`mailto:${email}`}
                                  className="hot-lead-contact-link"
                                >
                                  ✉ {email}
                                </a>
                              ))}
                            {Array.isArray(log.extracted_contacts.phone) &&
                              log.extracted_contacts.phone.map((phone) => (
                                <a
                                  key={`phone-${phone}`}
                                  href={`tel:${phone}`}
                                  className="hot-lead-contact-link"
                                >
                                  ☎ {phone}
                                </a>
                              ))}
                          </div>
                        )}
                      </td>
                      <td className="comment-text-cell">
                        <span className="comment-preview">{log.content?.substring(0, 80) || '—'}{log.content?.length > 80 ? '…' : ''}</span>
                      </td>
                      <td className="comment-text-cell">
                        {log.ai_response ? (
                          <span className="ai-response-preview">
                            <i className="bi bi-robot"></i> {log.ai_response.substring(0, 80)}{log.ai_response.length > 80 ? '…' : ''}
                          </span>
                        ) : (
                          <span className="no-response">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`sentiment-badge ${log.sentiment || 'neutral'}`}>
                          {log.sentiment || 'N/A'}
                        </span>
                      </td>
                      <td>
                        {log.replied_at ? (
                          <span className="status-badge replied">✅ {t('analytics.social.replied', 'Replied')}</span>
                        ) : (
                          <span className="status-badge pending">⏳ {t('analytics.social.pending_reply', 'Pending')}</span>
                        )}
                      </td>
                      <td className="date-cell">
                        {log.created_at
                          ? new Date(log.created_at).toLocaleString(i18n.language === 'it' ? 'it-IT' : 'en-US', {
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                            })
                          : '—'}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Conversation Log Summary */}
      <div className="card">
        <h3>{t('analytics.recent_activity.title')}</h3>
        <div className="daily-trends">
          {stats.daily_trends.slice(-7).reverse().map((day, index) => (
            <div key={index} className="trend-row">
              <div className="date">{new Date(day.date).toLocaleDateString(i18n.language === 'it' ? 'it-IT' : 'en-US')}</div>
              <div className="trend-metrics">
                <span className="positive">+{day.positive}</span>
                <span className="neutral">={day.neutral}</span>
                <span className="negative">-{day.negative}</span>
                <span className="escalations">⚠ {day.escalations}</span>
              </div>
              <div className="trend-bar">
                <div 
                  className="bar-segment positive" 
                  style={{ width: `${(day.positive / day.total_conversations) * 100}%` }}
                />
                <div 
                  className="bar-segment neutral" 
                  style={{ width: `${(day.neutral / day.total_conversations) * 100}%` }}
                />
                <div 
                  className="bar-segment negative" 
                  style={{ width: `${(day.negative / day.total_conversations) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {commentModal?.platform === 'instagram' && (
        <PostComments
          postId={commentModal.postId}
          postCaption={commentModal.postCaption}
          initialAiPaused={commentModal.initialAiPaused}
          onClose={closeCommentThread}
        />
      )}
      {commentModal?.platform === 'facebook' && (
        <FBPostComments
          postId={commentModal.postId}
          postCaption={commentModal.postCaption}
          initialAiPaused={commentModal.initialAiPaused}
          onClose={closeCommentThread}
        />
      )}
    </div>
  );
}

export default AnalyticsDashboard;
